// @flow
import React, {useState, useEffect, useCallback} from 'react';
import SortableList from 'react-easy-sort';
import SubcategoryRow from './SubcategoryRow';
import SubcategoryModal from '../SubcategoryModal/SubcategoryModal';

import {Button, makeStyles} from '@material-ui/core';
import {arrayMove} from '../../../../utils/array';
import {actions} from '../../../sub-categories/duck';

import type {Subcategory, Category} from '../../../../entities';
import {useActions} from '../../../../utils/globalStore';
import {colors} from '../../../../assets/styles/colors';
import {useToggle} from '../../../../utils/react';
import {Add} from '@material-ui/icons';
import env from '../../../../environment';

const useStyles = makeStyles(theme => ({
  subCategoriesContainer: {
    marginLeft: '1rem',
    marginTop: '.5rem',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '.75rem',
    overflow: 'hidden',
    gap: '.5rem',
  },
  addSubcategoryButton: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: colors.grey200,
    padding: '.5rem',
    borderRadius: '.75rem',
    gap: '1rem',
    textTransform: 'none',
    textAlign: 'center',
    color: colors.grey800,
  },
  containerDraggable: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.25rem',
  },
}));

type Props = {
  category: Category,
  subcategories: Subcategory[],
};

export const SUBCATEGORY_ITEM_ENABLED = env.environment !== 'production';

function SubcategoryListComponent(props: Props) {
  const {category} = props;
  const classes = useStyles();

  const [subcategories, setSubcategories] = useState(props.subcategories);
  const editSubcategory = useActions(actions.editSubcategory);
  const createSubcategory = useActions(actions.createSubcategory);
  const deleteSubcategory = useActions(actions.deleteSubcategory);
  const sortSubcategories = useActions(actions.sortSubcategory);

  const [subcategorySelected, setSubcategorySelected] = useState(undefined);
  const [modalOpen, toggleModalOpen] = useToggle(false);

  useEffect(() => {
    setSubcategories(props.subcategories);
  }, [props.subcategories]);

  const handleSortEnd = useCallback(
    (oldIndex: number, newIndex: number) => {
      const oldId = subcategories[oldIndex].id;
      const newId = subcategories[newIndex].id;

      const sortedSections = arrayMove(subcategories, oldIndex, newIndex);

      const movement = {
        end: {
          id: newId,
          pos: newIndex,
        },
        init: {
          id: oldId,
          pos: oldIndex,
        },
      };

      sortSubcategories({
        subcategoryRequest: {movement, categoryId: category.id},
      });
      setSubcategories(sortedSections);
    },
    [subcategories, sortSubcategories, category.id]
  );

  const handleDeleteSubcategory = useCallback(
    (subcategoryId: string) => {
      deleteSubcategory({
        subcategoryRequest: {
          id: subcategoryId,
        },
      });
    },
    [deleteSubcategory]
  );

  const handleEditSubcategory = useCallback(
    (subcategory: Subcategory) => {
      setSubcategorySelected(subcategory);
      toggleModalOpen();
    },
    [toggleModalOpen]
  );

  const handleAddSubcategory = useCallback(() => {
    setSubcategorySelected(undefined);
    toggleModalOpen();
  }, [toggleModalOpen]);

  return (
    <section className={classes.subCategoriesContainer}>
      <SortableList
        className={classes.containerDraggable}
        draggedItemClassName="dragged"
        onSortEnd={handleSortEnd}
      >
        {subcategories?.map(subcategory => (
          <SubcategoryRow
            key={subcategory.id}
            subcategory={subcategory}
            onDeleteSubcategory={handleDeleteSubcategory}
            onEditSubcategory={handleEditSubcategory}
          />
        ))}
      </SortableList>
      <Button
        className={classes.addSubcategoryButton}
        startIcon={<Add />}
        onClick={handleAddSubcategory}
      >
        Agregar subcategoría
      </Button>
      <SubcategoryModal
        open={modalOpen}
        handleClose={() => {
          setSubcategorySelected(undefined);
          toggleModalOpen();
        }}
        categories={[category]}
        editSubcategory={editSubcategory}
        createSubcategory={createSubcategory}
        subcategory={
          subcategorySelected && {
            category,
            subcategory: subcategorySelected,
          }
        }
      />
    </section>
  );
}

const SubcategoryList = SubcategoryListComponent;
export default SubcategoryList;
